"use client";

import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  memo,
  useLayoutEffect,
  useMemo,
} from "react";
import "./libs/leaflet";
import { MapContainer, FeatureGroup, useMapEvents } from "react-leaflet";
import { BaseLayersControl } from "./baseLayers.jsx";

const DEFAULT_MAP_EVENTS = [
  "pm:create",
  "pm:edit",
  "pm:remove",
  "pm:drawstart",
  "pm:drawend",
  "pm:vertexadded",
  "pm:vertexremoved",
  "pm:dragstart",
  "pm:drag",
  "pm:dragend",
  "pm:markerdragstart",
  "pm:markerdragend",
  "pm:cut",
  "pm:change",
];

const MapEventHandler = memo(function MapEventHandler({ onClick }) {
  const handleClick = useCallback(
    (e) => {
      const { lat, lng } = e.latlng;
      onClick?.(lat, lng);
    },
    [onClick],
  );

  useMapEvents({ click: handleClick });
  return null;
});

function matchesFilter(eventName, { include, exclude } = {}) {
  if (!eventName) return false;
  if (Array.isArray(exclude) && exclude.includes(eventName)) return false;
  if (Array.isArray(include) && include.length > 0)
    return include.includes(eventName);
  return true;
}

function getHeightClasses(preset) {
  switch (preset) {
    case "modal":
      return "h-[70vh] min-h-[320px] max-h-[calc(100vh-10rem)]";
    case "page":
      return "h-[60vh] min-h-[420px]";
    case "compact":
      return "h-[360px] min-h-[280px]";
    case "full":
      return "h-full min-h-0";
    default:
      return "h-[520px] min-h-[360px]";
  }
}

/**
 * Mapa base reutilizable construido sobre React Leaflet.
 *
 * Características:
 * - Manejo centralizado de eventos del mapa.
 * - Sistema de slots para renderizar controles personalizados.
 * - Soporte para FeatureGroup compartido.
 * - Integración con capas base configurables.
 * - API imperativa mediante ref.
 * - Suscripción a eventos Geoman y Leaflet.
 *
 * @component
 *
 * @param {(lat:number,lng:number)=>void} [props.onClick]
 * Se ejecuta al hacer click sobre el mapa.
 *
 * @param {React.RefObject} [props.editRef]
 * FeatureGroup externo utilizado para herramientas de edición.
 * Si no se proporciona, el componente crea uno internamente.
 *
 * @param {(mapInstance:Object)=>void} [props.onReady]
 * Se ejecuta cuando el mapa termina de inicializarse.
 *
 * @param {React.ReactNode} [props.children]
 * Elementos React renderizados sobre el mapa.
 *
 * @param {Array} [props.layers=[]]
 * Capas adicionales utilizadas por BaseLayersControl.
 *
 * @param {(eventName:string,event:Object)=>void} [props.onMapEvent]
 * Callback global ejecutado para todos los eventos registrados.
 *
 * @param {boolean} [props.useWrapper=true]
 * Indica si el mapa debe renderizarse dentro de un contenedor
 * con altura administrada automáticamente.
 *
 * @param {"default"|"modal"|"page"|"compact"|"full"} [props.heightPreset="default"]
 * Preset de altura del contenedor.
 *
 * Opciones:
 * - "default"
 * - "modal"
 * - "page"
 * - "compact"
 * - "full"
 *
 * @param {string} [props.height]
 * Clase Tailwind personalizada para la altura.
 *
 * Ejemplo:
 * "h-[500px]"
 *
 * @param {string} [props.minHeight]
 * Clase Tailwind personalizada para altura mínima.
 *
 * Ejemplo:
 * "min-h-[400px]"
 *
 * @param {string} [props.className=""]
 * Clases adicionales aplicadas al contenedor principal.
 *
 * @param {"default"|"minimal"} [props.baseLayerMode="default"]
 * Modo de visualización del selector de capas base.
 *
 * @param {"satellite"|"satellite-labels"|"street"|"osm"} [props.baseLayerId]
 * Capa base inicial del mapa.
 *
 * Opciones disponibles:
 * - "satellite"
 * - "satellite-labels"
 * - "street"
 * - "osm"
 *
 * @example
 * <Map
 *   baseLayerId="satellite-labels"
 *   heightPreset="page"
 *   onClick={(lat, lng) => {
 *     console.log(lat, lng);
 *   }}
 * />
 *
 * @ref
 * El componente expone una API imperativa mediante ref:
 *
 * mapRef.current.map
 * Instancia de Leaflet.
 *
 * mapRef.current.featureGroup
 * Referencia al FeatureGroup utilizado por Geoman.
 *
 * mapRef.current.setSlot(name, node)
 * Agrega o reemplaza un slot visual.
 *
 * mapRef.current.clearSlot(name)
 * Elimina un slot visual.
 *
 * mapRef.current.clearAllSlots()
 * Elimina todos los slots.
 *
 * mapRef.current.subscribeMapEvents(handler, options)
 * Permite suscribirse a eventos del mapa.
 *
 * Opciones:
 * {
 *   include?: string[],
 *   exclude?: string[],
 *   throttleRaf?: boolean
 * }
 *
 * mapRef.current.getLastEvent()
 * Obtiene el último evento emitido.
 *
 * @events
 * Eventos emitidos por defecto:
 *
 * - pm:create
 * - pm:edit
 * - pm:remove
 * - pm:drawstart
 * - pm:drawend
 * - pm:vertexadded
 * - pm:vertexremoved
 * - pm:dragstart
 * - pm:drag
 * - pm:dragend
 * - pm:markerdragstart
 * - pm:markerdragend
 * - pm:cut
 * - pm:change
 */
const Map = forwardRef((props, ref) => {
  const {
    onClick,
    editRef,
    onReady,
    children,
    layers = [],
    onMapEvent,

    useWrapper = true,
    heightPreset = "default",
    height,
    minHeight,
    className = "",

    baseLayerMode = "default",
    baseLayerId,
  } = props;

  const [mapInstance, setMapInstance] = useState(null);
  const localFeatureGroupRef = useRef(null);

  const featureGroupRef = useMemo(
    () => editRef ?? localFeatureGroupRef,
    [editRef],
  );

  const position = [-1.071208, -80.486586];

  const uiSlotsRef = useRef(new globalThis.Map());
  const [slots, setSlots] = useState([]);

  const updateSlots = useCallback(() => {
    setSlots(Array.from(uiSlotsRef.current.entries()));
  }, []);

  const setSlot = useCallback(
    (name, node) => {
      uiSlotsRef.current.set(name, node);
      updateSlots();
    },
    [updateSlots],
  );

  const clearSlot = useCallback(
    (name) => {
      uiSlotsRef.current.delete(name);
      updateSlots();
    },
    [updateSlots],
  );

  const clearAllSlots = useCallback(() => {
    uiSlotsRef.current.clear();
    updateSlots();
  }, [updateSlots]);

  const subscribersRef = useRef(new Set());
  const lastEventRef = useRef(null);

  // { id: number, pending: {sub, eventName, e} | null }
  const rafRef = useRef({ id: 0, pending: null });

  const subscribeMapEvents = useCallback((handler, options = {}) => {
    if (typeof handler !== "function") return () => {};

    const entry = {
      handler,
      options: {
        include: options.include,
        exclude: options.exclude,
        throttleRaf: Boolean(options.throttleRaf),
      },
    };

    subscribersRef.current.add(entry);
    return () => subscribersRef.current.delete(entry);
  }, []);

  const emitEvent = useCallback(
    (eventName, e) => {
      lastEventRef.current = eventName;

      if (typeof onMapEvent === "function") {
        try {
          onMapEvent(eventName, e);
        } catch (err) {
          console.warn("onMapEvent falló:", err);
        }
      }

      for (const sub of subscribersRef.current) {
        if (!matchesFilter(eventName, sub.options)) continue;

        if (sub.options.throttleRaf) {
          rafRef.current.pending = { sub, eventName, e };

          if (!rafRef.current.id) {
            rafRef.current.id = requestAnimationFrame(() => {
              rafRef.current.id = 0;
              const p = rafRef.current.pending;
              rafRef.current.pending = null;
              if (!p) return;

              try {
                p.sub.handler(p.eventName, p.e);
              } catch (err) {
                console.warn("map subscriber falló:", err);
              }
            });
          }
          continue;
        }

        try {
          sub.handler(eventName, e);
        } catch (err) {
          console.warn("map subscriber falló:", err);
        }
      }
    },
    [onMapEvent],
  );

  const onReadyRef = useRef(onReady);
  useLayoutEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const readyCalledRef = useRef(false);
  const lastMapRef = useRef(null);

  useEffect(() => {
    const map = mapInstance?.target;

    // ✅ capturar para cleanup (resuelve warning rafRef.current)
    const rafState = rafRef.current;

    if (!map || !featureGroupRef.current) {
      readyCalledRef.current = false;
      lastMapRef.current = null;
      return () => {
        if (rafState.id) cancelAnimationFrame(rafState.id);
        rafState.id = 0;
        rafState.pending = null;
      };
    }

    if (lastMapRef.current && lastMapRef.current !== map) {
      readyCalledRef.current = false;
    }
    lastMapRef.current = map;

    const handler = (e) => {
      emitEvent(e?.type ?? null, e);
    };

    DEFAULT_MAP_EVENTS.forEach((evt) => {
      map.on(evt, handler);
    });

    if (!readyCalledRef.current) {
      readyCalledRef.current = true;

      try {
        onReadyRef.current?.(mapInstance);
      } catch (err) {
        console.warn("onReady falló:", err);
      }
    }

    return () => {
      DEFAULT_MAP_EVENTS.forEach((evt) => map.off(evt, handler));

      if (rafState.id) cancelAnimationFrame(rafState.id);
      rafState.id = 0;
      rafState.pending = null;

      readyCalledRef.current = false;
      lastMapRef.current = null;
    };
  }, [mapInstance, emitEvent, featureGroupRef]);

  useImperativeHandle(
    ref,
    () => ({
      map: mapInstance,
      featureGroup: featureGroupRef,

      setSlot,
      clearSlot,
      clearAllSlots,

      subscribeMapEvents,
      getLastEvent: () => lastEventRef.current,
    }),
    [
      mapInstance,
      featureGroupRef,
      setSlot,
      clearSlot,
      clearAllSlots,
      subscribeMapEvents,
    ],
  );

  const wrapperHeight =
    [height, minHeight].filter(Boolean).join(" ") ||
    getHeightClasses(heightPreset);

  const Wrapper = useWrapper ? "div" : React.Fragment;
  const wrapperProps = useWrapper
    ? { className: `relative w-full ${wrapperHeight} ${className}` }
    : {};

  return (
    <Wrapper {...wrapperProps}>
      <div className={`relative h-full w-full ${!useWrapper ? className : ""}`}>
        <MapContainer
          center={position}
          zoom={16}
          maxZoom={26}
          minZoom={16}
          scrollWheelZoom={true}
          whenReady={setMapInstance}
          className="h-full w-full"
        >
          <BaseLayersControl
            mode={baseLayerMode}
            layers={layers}
            defaultLayerId={baseLayerId}
          />

          <FeatureGroup ref={featureGroupRef} />
          <MapEventHandler onClick={onClick} />
        </MapContainer>

        {slots.map(([name, node]) => (
          <React.Fragment key={name}>{node}</React.Fragment>
        ))}

        {children}
      </div>
    </Wrapper>
  );
});

Map.displayName = "Map";
export default Map;
